<template>
  <div class="dashboard-fleet-status">
    <h3 class="fleet-title">
      {{ $t('org-dashboard.fleet-status') }}
      <v-btn x-small text color="primary" @click="$emit('view-detail')">{{ $t('org-dashboard.view-detail') }}</v-btn>
    </h3>
    <div class="fleet-body">
      <div class="fleet-donut-wrap">
        <highcharts v-if="chartOptions" :options="chartOptions" ref="chart" />
        <div class="fleet-donut-center">
          <span class="fleet-donut-value">{{ total }}</span>
          <span class="fleet-donut-label">Total</span>
        </div>
      </div>
      <div class="fleet-legend">
        <div v-for="(seg, i) in segments" :key="i" class="fleet-legend-item" :class="{'fleet-legend-item--alert': seg.alert}">
          <span class="fleet-legend-dot" :style="{backgroundColor: seg.color}" :class="{'fleet-legend-dot--pulse': seg.alert}"></span>
          <span :class="{'font-weight-bold': seg.alert, 'red--text': seg.alert}">{{ seg.label }} ({{ seg.percent }}%)</span>
        </div>
      </div>
      <div v-if="meterDevices > 0" class="fleet-meter-badge">
        <v-icon x-small color="#6B7280">mdi-cable-data</v-icon>
        {{ $t('org-dashboard.fleet-meter-devices') }}: {{ meterDevices }}
      </div>
    </div>
    <div v-if="alert" class="fleet-alert">
      <div class="fleet-alert-header">
        <v-icon small color="#B91C1C">mdi-bell-ring-outline</v-icon>
        <span class="fleet-alert-title">{{ $t('org-dashboard.critical-attention') }}</span>
      </div>
      <p class="fleet-alert-text">{{ alert }}</p>
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'

export default {
  name: 'DashboardFleetStatus',
  components: {
    highcharts: Chart,
  },
  props: {
    total: { type: Number, default: 0 },
    segments: { type: Array, default: () => [] },
    alert: { type: String, default: null },
    meterDevices: { type: Number, default: 0 },
  },

  computed: {
    chartOptions() {
      if (!this.segments || this.segments.length === 0) return null

      return {
        chart: {
          type: 'pie',
          height: 140,
          width: 140,
          backgroundColor: 'transparent',
          margin: [0, 0, 0, 0],
        },
        title: { text: '' },
        credits: { enabled: false },
        tooltip: {
          pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)',
        },
        plotOptions: {
          pie: {
            innerSize: '70%',
            dataLabels: { enabled: false },
            borderWidth: 0,
            states: { hover: { brightness: -0.05 } },
          },
        },
        series: [{
          data: this.segments.map(s => ({
            name: s.label,
            y: s.value,
            color: s.color,
          })),
        }],
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-fleet-status {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.fleet-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 8px;
  margin-bottom: 16px;
}

.fleet-body {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 16px;
}

.fleet-donut-wrap {
  flex-shrink: 0;
  position: relative;
}

.fleet-donut-center {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.fleet-donut-value {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.fleet-donut-label {
  font-size: 9px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.fleet-legend {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fleet-legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #4b5563;
}

.fleet-legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &--pulse {
    animation: pulse 2s infinite;
  }
}

.fleet-meter-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #6B7280;
  background: #f3f4f6;
  border-radius: 6px;
  padding: 4px 10px;
  margin-top: 8px;
}

.fleet-alert {
  margin-top: auto;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px;
}

.fleet-alert-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.fleet-alert-title {
  font-size: 11px;
  font-weight: 700;
  color: #B91C1C;
  text-transform: uppercase;
}

.fleet-alert-text {
  font-size: 11px;
  color: #dc2626;
  line-height: 1.4;
  margin: 0;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
</style>
